import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { uniq } from "lodash-es";
import { useNavigate } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  ExtendedThalamosUser,
  ExternalPatientLinkEnhanced,
  FileNote,
  FormContextData,
  FormContextMiniForm,
  PatientTimelineResponse,
  UUID,
} from "@thalamos/aspire-common";
import {
  COMPLETED_FORM_STATUSES,
  formIsApplication,
  formIsMedRec,
  formIsRecordOfDetention,
  getBaseFormTemplate,
  getLatestVersion,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../../api.js";
import { useGetFileNotes } from "../../../hooks/apiCalls.js";
import { routeFns } from "../../../routePaths.js";
import {
  admissionFormContextLogic,
  canCancelRedo,
  canContinueAdmissionForm,
  canRetractRedo,
  canStartAdmissionForm,
  userIsRedoAuthor,
  userSignsNextReplacementPart,
} from "../helpers/formContextLogic.js";
import { FormRow } from "../helpers/FormRow.js";
import { admissionFormSummary } from "./admissionFormSummary.js";
import { CancelDeMinimisDialog } from "./CancelDeMinimisDialog.js";
import { CancelRedoDialog } from "./CancelRedoDialog.js";
import { DeMinimisResponseModal } from "./DeMinimisResponseModal.js";
import { RequestRedoDialog } from "./RequestRedoDialog.js";
import { RetractRedoDialog } from "./RetractRedoDialog.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import { Banner, BannerList } from "~/components/design-system/index.js";
import { useExternalPatientLinks } from "~/hooks/ExternalPatientLink/useExternalPatientLinks.js";

export function AdmissionFormRow({
  form,
  formContext,
  createDraftFn,
  user,
  setPdfViewFormId,
  showDeleteConfirmation,
  showRequestAmendDialog,
  showRequestDeMinimisDialog,
  showRequestRedoDialog,
  startReplacementDraft,
  reloadFormContext,
  lastMerged,
  patientTimeline,
  greyedOut,
}: {
  form: FormContextMiniForm;
  formContext: Omit<FormContextData, "patient">;
  createDraftFn: () => Promise<void>;
  user: ExtendedThalamosUser;
  showDeleteConfirmation?: (formDraftId: UUID) => void;
  showRequestAmendDialog?: (formId: UUID, formTemplateId: string) => void;
  showRequestDeMinimisDialog?: (formId: UUID, formTemplateId: string) => void;
  showRequestRedoDialog?: (formId: UUID, formTemplateId: string) => void;
  // Create + open the draft for a newly-accepted redo replacement form.
  startReplacementDraft?: (formId: UUID) => void;
  reloadFormContext?: () => void;
  setPdfViewFormId?: (formId: string | null) => void;
  lastMerged?: string | undefined;
  patientTimeline: PatientTimelineResponse | null;
  // Presents the card as inactive context — see FormRow.
  greyedOut?: boolean;
}) {
  const {
    canLaunchApplication,
    canLaunchMedicalRecommendation,
    mayUploadFormsToExternalSystem,
    formData,
    completedRecordOfDetention,
    isAdmissionArchivedOrClosed,
  } = admissionFormContextLogic(formContext, user);

  // An archived or closed admission is read-only for redo: redo statuses remain
  // visible but no redo action may be taken from the front end.
  const redoActionsAvailable = !isAdmissionArchivedOrClosed;

  const {
    myFormWorkItem,
    canLaunchDraft,
    canRequestAmend,
    canRequestDeMinimis,
    canRequestRedo,
    canRedoApplicationUnderRedo,
    formNeedsMoreSignatures,
    isFormFinalised,
    isAmend,
    draftIdOnNextPart,
  } = formData.get(form.id) ?? {};

  const linkedForms = form.linkedForms
    .map((lf) =>
      formContext.forms.find((f) => f.id === lf.id && f.status !== "deleted"),
    )
    .filter(Boolean);

  const linkedH3s = linkedForms
    .filter((f): f is FormContextMiniForm => !!f)
    .filter(formIsRecordOfDetention);

  const isApplication = formIsApplication(form);

  const canUploadFormToExternalSystem =
    mayUploadFormsToExternalSystem &&
    COMPLETED_FORM_STATUSES.includes(form.status);

  const navigate = useNavigate();

  const template = getBaseFormTemplate(form)!;
  const latestVersion = getLatestVersion(form);
  const genericFormData =
    latestVersion.data.length > 0
      ? template!.extractGenericData(latestVersion.data)
      : { admissions: {} };
  const latestVersionSigned = latestVersion.signatures.length > 0;
  const anyVersionSigned = form.versions.some((v) => v.signatures.length > 0);

  const hasPdfUri = !!latestVersion.pdfUri;

  const { fileNotes, reloadFileNotes } = useGetFileNotes(form.id, user);

  const hasFileNotes = fileNotes && fileNotes.length > 0;

  // Find active de minimis file note targeting current user
  const activeDeMinimisNote: FileNote | undefined = useMemo(
    () =>
      fileNotes?.find(
        (fn) =>
          fn.deMinimisStatus &&
          ["requested", "accepted"].includes(fn.deMinimisStatus) &&
          fn.deMinimisTargetUserId === user.id,
      ),
    [fileNotes, user.id],
  );

  // Find de minimis note targeting another user (for info banner)
  const deMinimisNoteForOtherUser: FileNote | undefined = useMemo(
    () =>
      fileNotes?.find(
        (fn) =>
          fn.deMinimisStatus &&
          ["requested", "accepted"].includes(fn.deMinimisStatus) &&
          fn.deMinimisTargetUserId &&
          fn.deMinimisTargetUserId !== user.id,
      ),
    [fileNotes, user.id],
  );

  const deMinimisTargetUserName = useMemo(() => {
    if (!deMinimisNoteForOtherUser?.deMinimisTargetUserId) return undefined;
    const sig = getLatestVersion(form).signatures.find(
      (s) => s.userId === deMinimisNoteForOtherUser.deMinimisTargetUserId,
    );
    return sig?.userName ?? "the assigned user";
  }, [deMinimisNoteForOtherUser, form]);

  const [showDeMinimisResponseModal, setShowDeMinimisResponseModal] =
    useState(false);
  const [showCancelDeMinimisDialog, setShowCancelDeMinimisDialog] =
    useState(false);
  const [showRetractRedoDialog, setShowRetractRedoDialog] = useState(false);
  const [showStartRedoDialog, setShowStartRedoDialog] = useState(false);
  // Cancelling an in-progress redo requires a reason (DEV-2432). Reachable from
  // the original form's row ("Cancel redo") and the replacement's row ("Abandon
  // redo"); both cancel the same redo on the original, so both open this dialog.
  const [cancelRedoTarget, setCancelRedoTarget] = useState<
    { formId: UUID; successMessage: string } | undefined
  >(undefined);

  // A pending redo request on this form (for the author to accept/decline).
  const redoRequestWorkItem = (
    patientTimeline?.inProgressWork?.admissions?.find(
      (a) => a.id === form.formContextId,
    )?.workItems || formContext.workItems
  ).find((w) => w.formId === form.id && w.requestType === "redo");

  // True only when a redo was requested *directly* on this form (a redo work
  // item exists). A form can also enter "redo-requested" by inheriting the state
  // from an upstream form being redone (the dependency model) — that has no work
  // item and no reason, and is not something the owner accepts/declines here, so
  // it must not surface the "Respond to Redo" flow.
  const hasDirectRedoRequest = !!redoRequestWorkItem;

  // True when this form is being redone directly — a live replacement exists
  // (self-initiated or accepted both create one). A form that is "redo-in-progress"
  // only by inheriting the state from an upstream redo has no replacement of its
  // own, so it must not offer "Cancel redo" (cancellation belongs to the upstream).
  const inProgressRedoReplacement = formContext.forms.find(
    (f) => f.redoneFromFormId === form.id && f.status === "in-progress",
  );
  const hasInProgressRedoReplacement = !!inProgressRedoReplacement;

  // Find any active de minimis note on this form (for MHA admin cancel)
  const activeDeMinimisForCancel: FileNote | undefined = useMemo(
    () =>
      fileNotes?.find(
        (fn) =>
          fn.deMinimisStatus &&
          ["requested", "accepted"].includes(fn.deMinimisStatus),
      ),
    [fileNotes],
  );

  // Auto-open modal when current user has an active de minimis correction
  const hasAutoOpenedDeMinimisRef = useRef(false);
  useEffect(() => {
    if (activeDeMinimisNote && !hasAutoOpenedDeMinimisRef.current) {
      hasAutoOpenedDeMinimisRef.current = true;
      setShowDeMinimisResponseModal(true);
    }
  }, [activeDeMinimisNote]);

  useEffect(() => {
    reloadFileNotes();
  }, [formContext, form.id]);

  const currentUserIsAssigned =
    formContext.activeTeamworkWorkItem?.assignedUserId === user.id;

  // Retracting a redo needs a live claim on the admission, paired with
  // currentUserIsAssigned: the server 403s a caller who is not the assigned
  // holder of an accepted work item, so a weaker gate would offer doomed
  // actions. Responding to a redo does not — see RedoReviewPage.
  const admissionActivelyClaimed =
    formContext.activeTeamworkWorkItem?.status === "accepted";

  const isUserCreatedBy = form.ownerUserId === user.id;

  const isFormOwnerOrAssignedTeam =
    form.ownerUserId === user.id ||
    (!!user.sessionContext?.teamId &&
      user.sessionContext.teamId === form.ownerTeamId);

  // Redo authorship (Layer A): a med rec may only be redone — started, responded
  // to, cancelled — by its FIRST signatory (the lead doctor). For a joint med rec
  // only the Part-1 doctor authors the redo; the Part-2 doctor falls through to
  // the "Request Redo" branch (!isRedoAuthor). An application stays team-wide.
  // This gates the redo controls on the completed original form; the unsigned
  // replacement row's own actions (Abandon redo / Delete) are gated by
  // currentUserIsAssigned instead, as it has no signatures of its own yet.
  const isRedoAuthor = userIsRedoAuthor(
    form,
    user.id,
    isFormOwnerOrAssignedTeam,
  );

  const canUserContinue = canContinueAdmissionForm(
    draftIdOnNextPart!,
    currentUserIsAssigned,
    completedRecordOfDetention,
    isAmend,
  );

  const externalLinks = useExternalPatientLinks({
    patientId: formContext.patientId,
  });

  const uploadToRioExternalLink:
    ExternalPatientLinkEnhanced | null | undefined = useMemo(() => {
    if (canUploadFormToExternalSystem === false) {
      return null;
    }

    if (externalLinks.externalPatientLinksLoading === true) {
      return undefined;
    }

    const candidateLinks = (
      externalLinks.externalPatientLinks?.externalLinks ?? []
    ).filter(
      (externalPatientLink) => externalPatientLink.externalSystemType === "rio",
    );

    // The lowest number is the highest priority (i.e. 1 is higher priority than 2)
    const highestProirityLink = candidateLinks
      .filter((externalPatientLink) => externalPatientLink.canPush)
      .sort(
        (a, b) =>
          a.priorityWithinExternalSystem - b.priorityWithinExternalSystem,
      )?.[0];

    return highestProirityLink ?? null;
  }, [
    canUploadFormToExternalSystem,
    externalLinks.externalPatientLinks?.externalLinks,
    externalLinks.externalPatientLinksLoading,
  ]);

  const [uploadedToRioExternalLink, setUploadedToRioExternalLink] = useState<
    { uploaded: false } | { uploaded: true; date: string }
  >({ uploaded: false });

  const [{ data: externalPatientFormPushEvent }] =
    apiHooks.forms.getExternalPatientFormPushEvent(formContext.id, form.id);

  const shouldUploadToRioExternalLink =
    uploadToRioExternalLink !== undefined && uploadToRioExternalLink !== null;

  const externalPatientFormPushEventCreated =
    externalPatientFormPushEvent?.created;

  useEffect(() => {
    if (shouldUploadToRioExternalLink && externalPatientFormPushEventCreated) {
      setUploadedToRioExternalLink({
        uploaded: true,
        date: externalPatientFormPushEventCreated!,
      });
    }
  }, [externalPatientFormPushEventCreated, shouldUploadToRioExternalLink]);

  const isMedRec = formIsMedRec(form);

  const canDeleteUploadedForm =
    form.source === "manual-upload" &&
    currentUserIsAssigned &&
    isUserCreatedBy &&
    ((isMedRec && linkedForms.length === 0) ||
      (isApplication && linkedH3s.length === 0));

  const menuOptions: MenuOptionsType[] = [
    ...(latestVersionSigned
      ? [
          {
            icon: "download",
            onClick: async () => {
              const res = await api.forms.getPdf(form.id, false);
              const dataUri = `data:application/pdf;base64,${res.data}`;
              triggerDownload(dataUri, "form.pdf");
            },
            name: "Download",
            disabled: false,
          },
        ]
      : []),
    ...(latestVersionSigned && setPdfViewFormId
      ? [
          {
            icon: "view",
            onClick: async () => {
              setPdfViewFormId(form.id);
            },
            name: "View PDF",
            disabled: false,
          },
        ]
      : []),
    ...(latestVersionSigned &&
    uploadToRioExternalLink &&
    patientTimeline &&
    hasPdfUri
      ? [
          {
            name: "Upload to Rio",
            icon: uploadedToRioExternalLink.uploaded ? "uploadDone" : "upload",
            tooltip: uploadedToRioExternalLink.uploaded
              ? [
                  "Uploaded to Rio",
                  dayjs(uploadedToRioExternalLink.date).format(
                    "DD/MM/YYYY HH:mm",
                  ),
                ]
              : undefined,
            onClick: async () => {
              if (uploadToRioExternalLink) {
                await externalLinks.uploadFormPdfViaExternalPatientLink(
                  uploadToRioExternalLink,
                  [form.id],
                  { ...formContext, patient: patientTimeline.patient },
                );
              }
            },
            disabled: uploadToRioExternalLink === undefined,
          },
        ]
      : []),
    ...(formNeedsMoreSignatures && formContext.activeTeamworkWorkItem
      ? [
          {
            icon: "transfer",
            link: routeFns.formContextPageSendRequest(
              formContext.id,
              formContext.patientId,
              {
                formId: form.id,
                requestType: "complete",
                formPart: "1",
              },
            ),
            name: "Send",
            disabled: false,
          },
        ]
      : []),
    ...(redoActionsAvailable &&
    !anyVersionSigned &&
    currentUserIsAssigned &&
    isFormOwnerOrAssignedTeam &&
    form.redoneFromFormId
      ? [
          {
            // A redo replacement: abandon the redo (cancels it on the original
            // and discards this form) rather than a bare delete that would leave
            // the original stuck in redo-in-progress.
            name: "Abandon redo",
            onClick: () => {
              if (form.redoneFromFormId) {
                setCancelRedoTarget({
                  formId: form.redoneFromFormId,
                  successMessage: "Redo abandoned",
                });
              }
            },
            icon: "delete",
            disabled: false,
          },
        ]
      : []),
    ...(!anyVersionSigned &&
    currentUserIsAssigned &&
    !!showDeleteConfirmation &&
    isFormOwnerOrAssignedTeam &&
    !form.redoneFromFormId
      ? [
          {
            name: "Delete",
            onClick: () => showDeleteConfirmation(form.id),
            icon: "delete",
            disabled: false,
          },
        ]
      : []),
    ...(canRequestAmend && showRequestAmendDialog
      ? [
          {
            name: "Request Amend",
            onClick: () =>
              showRequestAmendDialog(form.id, form.formTemplate.id),
            icon: "edit",
            disabled: false,
          },
        ]
      : []),
    ...(redoActionsAvailable &&
    (canRequestRedo || canRedoApplicationUnderRedo) &&
    isRedoAuthor &&
    !hasDirectRedoRequest &&
    !hasInProgressRedoReplacement
      ? [
          {
            // The author redoes their own form: request + accept in one step
            // (straight to redo-in-progress), then open the new draft. Also the
            // escape hatch out of a soft-lock — an application that inherited a
            // redo state from an upstream med rec being redone can be redone
            // here directly, rather than waiting on the upstream to resolve.
            name: "Start redo",
            onClick: () => {
              setShowStartRedoDialog(true);
            },
            icon: "edit",
            disabled: false,
          },
        ]
      : []),
    ...(redoActionsAvailable &&
    canRequestRedo &&
    !isRedoAuthor &&
    showRequestRedoDialog
      ? [
          {
            // Someone else (e.g. the AMHP) asks the author to redo their form.
            name: "Request Redo",
            onClick: () => {
              showRequestRedoDialog(form.id, form.formTemplate.id);
            },
            icon: "edit",
            disabled: false,
          },
        ]
      : []),
    ...(canRetractRedo({
      redoActionsAvailable,
      currentUserIsAssigned,
      admissionActivelyClaimed,
      redoState: form.redoState,
      hasDirectRedoRequest,
      isRedoAuthor,
    })
      ? [
          {
            // Any holder of their team's claim may withdraw the pending
            // request, not just whoever raised it; the author responds
            // (accept/decline) instead of retracting.
            name: "Retract redo request",
            onClick: () => {
              setShowRetractRedoDialog(true);
            },
            icon: "cancel",
            disabled: false,
          },
        ]
      : []),
    ...(redoActionsAvailable &&
    currentUserIsAssigned &&
    form.redoState === "redo-in-progress" &&
    inProgressRedoReplacement &&
    userSignsNextReplacementPart(
      inProgressRedoReplacement,
      form,
      user.id,
      isFormOwnerOrAssignedTeam,
    ) &&
    startReplacementDraft
      ? [
          {
            // The in-progress replacement is collapsed into this (original) row,
            // so "Continue redo" opens/continues the new version's draft from
            // here rather than from a separate in-progress row. It is offered to
            // whichever author signs the replacement's next part: the lead doctor
            // for part 1, and — for a joint med rec — the second author for part
            // 2, giving that author a route back if they leave and return.
            name: "Continue redo",
            onClick: () => {
              startReplacementDraft(inProgressRedoReplacement.id);
            },
            icon: "edit",
            disabled: false,
          },
        ]
      : []),
    ...(canCancelRedo({
      form,
      contextForms: formContext.forms,
      redoActionsAvailable,
      currentUserIsAssigned,
      isRedoAuthor,
      inProgressRedoReplacement,
    })
      ? [
          {
            name: "Cancel redo",
            onClick: () => {
              setCancelRedoTarget({
                formId: form.id,
                successMessage: "Redo cancelled",
              });
            },
            icon: "cancel",
            disabled: false,
          },
        ]
      : []),
    ...(canRequestDeMinimis &&
    showRequestDeMinimisDialog &&
    !activeDeMinimisForCancel
      ? [
          {
            name: "Request De Minimis",
            onClick: () =>
              showRequestDeMinimisDialog(form.id, form.formTemplate.id),
            icon: "edit",
            disabled: false,
          },
        ]
      : []),
    ...(activeDeMinimisNote
      ? [
          {
            name: "Respond to De Minimis",
            onClick: () => setShowDeMinimisResponseModal(true),
            icon: "edit",
            disabled: false,
          },
        ]
      : []),
    ...(user.sessionContext?.teamType === "mha" && activeDeMinimisForCancel
      ? [
          {
            icon: "delete",
            onClick: () => setShowCancelDeMinimisDialog(true),
            name: "Cancel De Minimis",
            disabled: false,
          },
        ]
      : []),
    ...(canUserContinue
      ? [
          {
            icon: "edit",
            link: routeFns.formDraftsComplete(
              draftIdOnNextPart!,
              formContext.patientId,
            ),
            name: "Continue",
            disabled: false,
          },
        ]
      : []),
    ...(canStartAdmissionForm(
      canLaunchDraft,
      myFormWorkItem,
      currentUserIsAssigned,
    )
      ? [
          {
            icon: "edit",
            onClick: async () => {
              await createDraftFn();
            },
            name: "Start",
            disabled: false,
          },
        ]
      : []),
    ...(draftIdOnNextPart &&
    (canLaunchMedicalRecommendation || canLaunchApplication)
      ? [
          {
            icon: "cancel",
            link: routeFns.formContextReasonForNotProceedingDialog(
              formContext.id,
              formContext.patientId,
            ),
            name: canLaunchApplication ? "No application" : "No recommendation",
            disabled: false,
          },
        ]
      : []),
    ...(currentUserIsAssigned &&
    !isFormFinalised &&
    user.sessionContext?.teamType === "mha"
      ? [
          {
            icon: "add",
            onClick: () => {
              return navigate(
                routeFns.formContextAddFileNoteDialog(
                  formContext.id,
                  formContext.patientId,
                  { formId: form.id },
                ),
              );
            },
            name: "Add file note",
            disabled: false,
          },
        ]
      : []),
    ...(currentUserIsAssigned &&
    hasFileNotes &&
    !isFormFinalised &&
    user.sessionContext?.teamType === "mha"
      ? [
          {
            icon: "delete",
            onClick: () => {
              return navigate(
                routeFns.formContextDeleteFileNoteDialog(
                  formContext.id,
                  formContext.patientId,
                  { formId: form.id },
                ),
              );
            },
            name: "Delete file note(s)",
            disabled: false,
          },
        ]
      : []),
    ...(canDeleteUploadedForm
      ? [
          {
            icon: "delete",
            link: routeFns.formContextDeleteUploadedFormDialog(
              formContext.id,
              formContext.patientId,
              { formId: form.id },
            ),
            name: "Delete uploaded form",
            disabled: false,
          },
        ]
      : []),
  ];

  const theme = useTheme();

  // The form may have been re-linked from another form context, so a work item
  // raised on it is more likely to be found on the patient timeline than in this
  // context.
  const summaryWorkItems =
    patientTimeline?.inProgressWork?.admissions?.find(
      (a) => a.id === form.formContextId,
    )?.workItems || formContext.workItems;

  const formChipData = genericFormData?.admissions || {};
  let formDataChips: string[] = [];
  if ("applicationHospitalName" in formChipData) {
    const hospitalName = formChipData.applicationHospitalName;
    formDataChips.push(`Hospital Name: ${hospitalName}`);
  }
  if ("admissionHospitalName" in formChipData) {
    formDataChips.push(`Hospital Name: ${formChipData.admissionHospitalName}`);
  }
  if ("section12ApprovedStatus" in formChipData) {
    const s12status = formChipData.section12ApprovedStatus!.some((s) => s);
    formDataChips.push(`Section 12 Approved: ${s12status ? "Yes" : "No"}`);
  }
  if ("applicationHospitalName" in formChipData) {
    formDataChips.push(
      `Hospital Name: ${formChipData.applicationHospitalName}`,
    );
  }
  if ("lastExaminedDate" in formChipData) {
    formDataChips.push(
      ...(formChipData.lastExaminedDate ?? []).map(
        (d) => `Last Examined Date: ${dayjs(d).format("DD/MM/YYYY")}`,
      ),
    );
  }

  const isFormManualUploaded = form.source === "manual-upload";

  const firstSignature = latestVersion.signatures[0];
  const signedByText =
    firstSignature !== undefined &&
    `${
      isFormManualUploaded
        ? `Form uploaded by ${firstSignature.userName}`
        : `Signed by ${latestVersion.signatures
            .map((s) => s.userName)
            .join(", ")}`
    } `;

  return (
    <>
      <externalLinks.Component />
      {deMinimisNoteForOtherUser && deMinimisTargetUserName && (
        <Banner
          title={`A de minimis correction is awaiting action by ${deMinimisTargetUserName}. They need to log in to complete this correction.`}
          bannerType={BannerList.WARNING}
        />
      )}
      <FormRow
        menuOptions={menuOptions}
        formDataChips={uniq(formDataChips)}
        heading={`Section ${template!.section} - ${template!.description}`}
        subHeading={`${signedByText || ""}Last updated: ${dayjs
          .utc(form.updated)
          .fromNow()}`}
        summary={admissionFormSummary({
          theme,
          form,
          template,
          workItems: summaryWorkItems,
          isMedRec,
          hasDirectRedoRequest,
        })}
        lastMerged={lastMerged}
        formUploadedAt={isFormManualUploaded ? form.created : undefined}
        greyedOut={greyedOut}
      />
      {showDeMinimisResponseModal && activeDeMinimisNote && (
        <DeMinimisResponseModal
          formId={form.id}
          fileNote={activeDeMinimisNote}
          formContext={formContext}
          onClose={() => setShowDeMinimisResponseModal(false)}
          reloadFormContext={() => {
            reloadFileNotes();
            reloadFormContext?.();
          }}
          userName={user.name}
          userDefaultSignature={user.defaultSignature}
        />
      )}
      {showStartRedoDialog && (
        <RequestRedoDialog
          mode="start"
          formId={form.id}
          formTemplateId={form.formTemplate.id}
          formContext={formContext}
          onClose={() => {
            setShowStartRedoDialog(false);
          }}
          reloadFormContext={() => reloadFormContext?.()}
          onStarted={(newFormId) => {
            setShowStartRedoDialog(false);
            startReplacementDraft?.(newFormId);
          }}
        />
      )}
      {showRetractRedoDialog && (
        <RetractRedoDialog
          formId={form.id}
          onClose={() => {
            setShowRetractRedoDialog(false);
          }}
          onSuccess={() => {
            setShowRetractRedoDialog(false);
            reloadFormContext?.();
            navigate(
              routeFns.formContextPage(formContext.id, formContext.patientId),
            );
          }}
        />
      )}
      {cancelRedoTarget && (
        <CancelRedoDialog
          formId={cancelRedoTarget.formId}
          successMessage={cancelRedoTarget.successMessage}
          onClose={() => {
            setCancelRedoTarget(undefined);
          }}
          onSuccess={() => {
            setCancelRedoTarget(undefined);
            reloadFormContext?.();
            navigate(
              routeFns.formContextPage(formContext.id, formContext.patientId),
            );
          }}
        />
      )}
      {showCancelDeMinimisDialog && activeDeMinimisForCancel && (
        <CancelDeMinimisDialog
          formId={form.id}
          fileNoteId={Number(activeDeMinimisForCancel.fileNoteId)}
          onClose={() => setShowCancelDeMinimisDialog(false)}
          onSuccess={() => {
            setShowCancelDeMinimisDialog(false);
            reloadFileNotes();
            reloadFormContext?.();
          }}
        />
      )}
    </>
  );
}
